import React, { useEffect, useState } from 'react';
import { FiCheckCircle, FiUserCheck } from 'react-icons/fi';
import Button from './atoms/Button';
import { DecodedItemData, DecodedSellerQRData, DecodedOrderQrData } from '../store/apis/orders';
import { parseFriendlyDateAndHour } from '../utils/dates';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import Checkbox from './atoms/Checkbox';
import { getStatusColor } from '../utils/theme-utils';

type ScanResultType =
  | 'success_confirmed'
  | 'success_unconfirmed'
  | 'error'
  | 'used'
  | 'seller_qr_unconfirmed'
  | 'seller_qr_confirmed'
  | 'scan_period_expired'
  | 'order_qr'
  | 'wrong_gate';

interface ScanResultCardProps {
  type: ScanResultType;
  qrData: DecodedItemData | DecodedSellerQRData | DecodedOrderQrData | null;
  onRetry: () => void;
  onConfirm?: () => void;
  onConfirmSellerQR?: () => void;
  onConfirmOrderQR?: (itemIds: string[]) => void;
  mismatchedShowtime?: boolean;
}

const QrResponse: React.FC<ScanResultCardProps> = ({
  type,
  qrData,
  onRetry,
  onConfirm,
  onConfirmSellerQR,
  onConfirmOrderQR,
  mismatchedShowtime,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { event } = useGetSelectedEvent();
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      e.preventDefault();

      if (e.key === 'Enter') {
        if (onConfirm && (type === 'success_unconfirmed' || type === 'seller_qr_unconfirmed')) {
          onConfirm();
        } else if (onConfirmSellerQR && type === 'seller_qr_unconfirmed') {
          onConfirmSellerQR();
        } else if (type === 'order_qr' && onConfirmOrderQR && selectedItems.length > 0) {
          onConfirmOrderQR(selectedItems);
        } else if (onRetry) {
          onRetry();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [type, onConfirm, onConfirmOrderQR, selectedItems]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [type]);

  const successStyles = getStatusColor('success');
  const errorStyles = getStatusColor('error');
  const infoStyles = getStatusColor('info');
  const warningStyles = getStatusColor('warning');

  const config = {
    success_confirmed: {
      bgColor: successStyles.bg,
      iconColor: successStyles.icon,
      title: '¡Escaneo exitoso!',
      icon: <FiCheckCircle className="w-20 h-20" />,
    },
    seller_qr_confirmed: {
      bgColor: infoStyles.bg,
      iconColor: successStyles.icon,
      title: 'Código RR.PP confirmado',
      icon: <FiCheckCircle className="w-20 h-20" />,
    },
    success_unconfirmed: {
      bgColor: 'bg-surface-base',
      iconColor: successStyles.icon,
      title: 'Código escaneado',
      icon: <FiUserCheck className="w-20 h-20" />,
    },
    order_qr: {
      bgColor: 'bg-surface-base',
      iconColor: successStyles.icon,
      title: 'Código escaneado',
      icon: <FiUserCheck className="w-20 h-20" />,
    },
    seller_qr_unconfirmed: {
      bgColor: 'bg-surface-base',
      iconColor: successStyles.icon,
      title: 'Código RR.PP escaneado',
      icon: <FiUserCheck className="w-20 h-20" />,
    },
    error: {
      bgColor: errorStyles.bg,
      iconColor: errorStyles.icon,
      title: '¡Escaneo erróneo!',
      icon: (
        <svg
          className="w-20 h-20"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    used: {
      bgColor: warningStyles.bg,
      iconColor: 'text-warning-300',
      title: '¡QR usado!',
      icon: (
        <svg
          className="w-20 h-20"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    scan_period_expired: {
      bgColor: 'bg-yellow-100',
      iconColor: 'text-warning-300',
      title: '¡Código expirado!',
      icon: <FiUserCheck className="w-20 h-20" />,
    },
    wrong_gate: {
      bgColor: warningStyles.bg,
      iconColor: 'text-warning-300',
      title: 'Puerta Equivocada',
      icon: (
        <svg
          className="w-20 h-20"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
  };

  const {
    bgColor, iconColor, title, icon,
  } = config[type as keyof typeof config];

  const handleItemSelect = (itemId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedItems((prev) => [...prev, itemId]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId));
    }
  };

  return (
    <div className={`${bgColor} w-full flex flex-col min-h-screen`}>
      <div className="flex-1">
        <div className="flex justify-center mb-4">
          <div className="mt-6 rounded-full p-8 bg-surface-base shadow-sm">
            <div className={mismatchedShowtime ? 'text-primary-500' : iconColor}>{icon}</div>
          </div>
        </div>
        <h2 className={`text-center text-3xl font-medium ${type === 'order_qr' ? 'mb-6' : 'mb-6'}`}>{title}</h2>

        {type === 'success_unconfirmed'
          && qrData
          && 'order_status' in qrData && (
            <div className="flex flex-col items-center justify-center gap-4">
              <p className="text-center text-xl text-text-secondary mb-2">
                {qrData?.ticket?.name}
              </p>
              <p className="text-center text-4xl font-medium text-text-secondary">
                {`${qrData.name} ${qrData.last_name}`.toUpperCase()}
              </p>
              <p className="text-center text-4xl font-medium text-text-secondary">
                {qrData.person_id}
              </p>
              {(event?.showtimes && event?.showtimes.length > 0) && !mismatchedShowtime && (
                <p className="text-center text-2xl font-medium text-text-secondary">
                  Función: {parseFriendlyDateAndHour(event?.showtimes?.find((st) => st.id === qrData.showtime_id)?.starts_at || '', true)}hs.
                </p>
              )}
              {mismatchedShowtime && (
                <div className="bg-primary-500 text-white w-full p-4 my-4 text-center text-xl font-medium">
                  La entrada NO pertenece a la función correspondiente <br />
                  {event?.showtimes && event?.showtimes.length > 0 && (
                    <p className="text-center text-2xl font-medium mt-2">
                      Función: {parseFriendlyDateAndHour(event?.showtimes?.find((st) => st.id === qrData.showtime_id)?.starts_at || '', true)}hs.
                    </p>
                  )}
                </div>)}
              {qrData.seat_name && (
                <p className="text-center text-4xl font-medium text-text-secondary">
                  {qrData.seat_name}
                </p>
              )}
              {qrData.audience_discount && (
                <div>
                  <p className="text-center text-4xl font-medium text-text-secondary">
                    Descuento por audiencia: {qrData.audience_discount.name}
                  </p>
                  <p className="text-center text-4xl font-medium text-text-secondary">
                    Requisitos: {qrData.audience_discount.requirements}
                  </p>
                </div>
              )}
              <div className="flex items-center justify-center w-full px-4">
                <Button
                  onClick={onConfirm}
                >
                  Aceptar {mismatchedShowtime && 'de todos modos'}
                </Button>
              </div>
              <div className="flex items-center justify-center w-full px-4">
                <Button variant="tertiary" onClick={onRetry}>
                  Cancelar
                </Button>
              </div>
            </div>)}

        {type === 'seller_qr_unconfirmed'
          && qrData
          && 'user_event' in qrData && (
            <div className="flex flex-col items-center justify-center gap-4">
              <p className="text-center text-xl text-text-secondary mb-2">
                Código Promocional RR.PP
              </p>
              <p className="text-center text-4xl font-medium text-text-secondary">
                {`${qrData.user_event.user.username}`.toUpperCase()}
              </p>
              <div className="flex items-center justify-center w-full px-4">
                <Button
                  onClick={onConfirm}
                >
                  Aceptar
                </Button>
              </div>
              <div className="flex items-center justify-center w-full px-4">
                <Button variant="tertiary" onClick={onRetry}>
                  Cancelar
                </Button>
              </div>
            </div>)}

        {type === 'seller_qr_confirmed' && qrData && 'user_event' in qrData && (
          <div className="flex flex-col items-center justify-center gap-4 pb-20">
            <p className="text-center text-xl text-text-secondary mb-2">
              Código Promocional RR.PP confirmado
            </p>
            <p className="text-center text-4xl font-medium text-text-secondary">
              {`${qrData.user_event.user.username}`.toUpperCase()}
            </p>
          </div>
        )}

        {type === 'success_confirmed' && qrData && 'order_status' in qrData && (
          <div className="flex flex-col items-center gap-4 min-h-screen">
            <p className="text-center text-xl text-text-secondary mb-2">
              {qrData?.ticket?.name}
            </p>
            <p className="text-center text-4xl font-medium text-text-secondary">
              {`${qrData.name} ${qrData.last_name}`.toUpperCase()}
            </p>
            <p className="text-center text-4xl font-medium text-text-secondary">
              {qrData.person_id}
            </p>
          </div>
        )}

        {type === 'error' && (
          <p className="text-center text-2xl text-text-secondary mb-4 font-medium">
            No se pudo leer el código solicitado
          </p>
        )}

        {type === 'wrong_gate' && (
          <p className="text-center text-2xl text-text-secondary mb-4 font-medium">
            Este escáner no tiene permiso para escanear esta entrada
          </p>
        )}

        {type === 'used' && qrData && 'used_at' in qrData && (
          <p className="flex flex-col items-center justify-center text-center text-2xl text-text-secondary mb-4 font-medium gap-2">
            <p>El código ya fue previamente escaneado:{' '}</p>
            <p>{parseFriendlyDateAndHour(qrData.used_at, true)}hs.</p>
          </p>
        )}

        {type === 'scan_period_expired' && qrData && 'ticket' in qrData && qrData.ticket?.scanning_ends_at && (
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="text-center text-2xl text-text-secondary font-medium">
              <p>El código es válido hasta: {parseFriendlyDateAndHour(qrData.ticket?.scanning_ends_at, true)}hs.</p>
              {qrData.ticket?.expired_message && (
                <p className="text-center text-2xl text-text-secondary font-medium mt-4">
                  {qrData.ticket?.expired_message}
                </p>
              )}
            </div>
            <Button onClick={onConfirm}
            >
              Aceptar
            </Button>
            <div className="flex items-center justify-center">
              <Button variant="tertiary" onClick={onRetry}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {type === 'order_qr' && qrData && 'items' in qrData && (
          <div className="flex flex-col items-center justify-center">
            <p className="text-center text-xl text-text-secondary">
              Orden #{qrData.display_id}
            </p>
            <p className="text-center text-xl text-text-secondary">
              Cantidad: {qrData.items.length}
            </p>
            <div className="flex flex-col w-full px-4 mt-4">
              {qrData.items.map((item, index) => (
                <div
                  key={item.item_id}
                  className={`px-4 ${item.used_at ? 'bg-surface-secondary' : 'bg-surface-base'} ${index === 0 ? 'border-y' : 'border-b'} border-border-primary w-full`}
                >
                  <div className="flex items-center gap-8">
                    <Checkbox
                      isChecked={(selectedItems.includes(item.item_id)) || !!item.used_at}
                      onCheckChange={(isChecked) => handleItemSelect(item.item_id, isChecked)}
                      size="large"
                      disabled={!!item.used_at}
                    />
                    <div>
                      <p className="text-lg font-medium">
                        {`${item.name} ${item.last_name}`.toUpperCase()}
                      </p>
                      {item.ticket && (
                        <p>{item.ticket.name} - {!!(item.ticket?.scanning_ends_at && new Date(item.ticket.scanning_ends_at) < new Date()) && <span className="text-error-600">VENCIDO</span>}</p>
                      )}
                      <p className="text-text-secondary">
                        {item.used_at
                          ? `Usado: ${parseFriendlyDateAndHour(item.used_at, true)}hs`
                          : 'No usado'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {event?.showtimes && event?.showtimes.length > 0 && !mismatchedShowtime && (
              <p className="text-center text-xl text-text-secondary mt-4">
                Función: {parseFriendlyDateAndHour(event?.showtimes?.find((st) => st.id === qrData.items[0].ticket?.showtime_id)?.starts_at || '', true)}hs.
              </p>
            )}
            {mismatchedShowtime && (
              <div className="bg-primary-500 text-white w-full p-4 my-4 text-center text-xl font-medium">
                La entrada NO pertenece a la función correspondiente <br />
                {event?.showtimes && event?.showtimes.length > 0 && (
                  <p className="text-center text-2xl font-medium mt-2">
                    Función: {parseFriendlyDateAndHour(event?.showtimes?.find((st) => st.id === qrData.items[0].ticket?.showtime_id)?.starts_at || '', true)}hs.
                  </p>
                )}
              </div>)}

            {qrData.items.some((item) => !item.used_at) && (
              <div className="sticky bottom-0 pt-4 bg-inherit">
                <Button
                  onClick={() => {
                    if (selectedItems.length > 0) {
                      onConfirmOrderQR?.(selectedItems);
                    }
                  }}
                  disabled={selectedItems.length === 0}
                  className={`
                    text-lg h-14 w-full py-2 px-4 border border-border-primary rounded shadow transition duration-200
                    ${selectedItems.length > 0 ? 'bg-success-100 hover:bg-opacity-50' : 'bg-surface-secondary cursor-not-allowed'}
                  `}
                >
                  Marcar seleccionados como ingresados ({selectedItems.length})
                </Button>
              </div>
            )}
            <div className="flex items-center justify-center mt-4">
              <Button variant="tertiary" className="text-lg h-14" onClick={onRetry}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {(!type.includes('success') && !type.includes('seller_qr') && !type.includes('order_qr') && type !== 'scan_period_expired') && (
          <div className="sticky bottom-0 px-4 bg-inherit">
            <Button
              onClick={onRetry}
            >
              Reintentar
            </Button>
          </div>
        )}

      </div>
    </div>
  );
};

export default QrResponse;
